// Chat sidebar (M2-06, CHAT-UX; spec 06 "Navigation"), Slack's shape with topics: the panel
// header carries Saved and a "+" (new channel, new message, browse); then Unreads (only when
// there are any), Channels (every joined channel; by space only when asked or when there are
// many), Direct messages with each DM's presence dot, and Agent rooms (with the agents add-on;
// its caption's "+" is the one New agent room). The inbox is the rail's (UX pass 0.4). A channel with
// topics has a small disclosure chevron: open, its topics sit indented beneath it, pinned ones
// first, each with its unread count, closed ones greyed, and (for the channel's admins) an
// "Archived (n)" group at the end; the open channel's topics are open by default. Every topic
// action is one click away (CHAT-UX): a "+" over the channel row adds a topic; a topic row
// shows pin, close and "…" (rename, move, copy link, delete) on hover; an archived one shows
// Restore. Each section's "+" creates or browses. An unread row is bold with a pill; a muted
// channel shows none. The lists are the same cached queries the Chat pages read. With the chat
// add-on off the rows give way to a one-line note. CHAT-CONTROLS: Threads (the person's
// threads, with its unread count) sits beside Unreads and Activity; a channel row's "…" holds
// Channel details, Rename, Mute, Leave and Archive (the managers' items for managers only); the
// Channels "+" also opens Archived channels.
import {
  AvatarTile,
  Button,
  CountPill,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavList,
  NavRow,
  SidebarCaption,
  SidebarEmpty,
  SidebarPanel,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BellIcon,
  BellOffIcon,
  BookmarkIcon,
  BotIcon,
  BrushCleaningIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CompassIcon,
  EllipsisIcon,
  InfoIcon,
  LockIcon,
  LogOutIcon,
  MailOpenIcon,
  MessageSquarePlusIcon,
  MessagesSquareIcon,
  PencilIcon,
  PinIcon,
  PlusIcon,
  SearchIcon,
  Share2Icon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { SidebarProps } from "../../extensions/registry";
import { ChannelDetailsDialog, type DetailsTab } from "../../features/chat/channel-details";
import { useCanManage, usePrefs } from "../../features/chat/channel-settings";
import {
  archivedTopics,
  type ChannelGrouping,
  channelLabel,
  dmMemberIds,
  isDm,
  orderedTopics,
  readGrouping,
  realTopics,
  shownUnread,
  writeGrouping,
} from "../../features/chat/chat-model";
import {
  channelIcon,
  NewChannelDialog,
  NewMessageDialog,
  useChatLists,
} from "../../features/chat/chat-pages";
import {
  chatKeys,
  myThreadsQuery,
  type SidebarChannel,
  type TopicSummary,
  topicsQuery,
} from "../../features/chat/chat-queries";
import {
  DeAvatar,
  DeHoverCard,
  type DePresence as DePresenceRow,
  ringHint,
  useDePresence,
} from "../../features/chat/de-presence";
import { TeamSection } from "../../features/chat/de-sidebar";
import { DmCleanupDialog, useCloseDm, useReopenOnVisit } from "../../features/chat/dm-close";
import { StatusEmoji, useStatuses, useTimeZoneSync } from "../../features/chat/member-status";
import { prefetchChannel, prefetchTopic } from "../../features/chat/prefetch";
import { PresenceDot, presenceState } from "../../features/chat/presence-dot";
import { MyStatusRow } from "../../features/chat/set-status";
import { NewTopicDialog, TopicMenu, TopicQuickActions } from "../../features/chat/topic-actions";
import { type ChatContext, useChat, usePresence } from "../../features/chat/use-chat";
import { isOperatorOnly } from "../../features/company";
import { SuperAdminSpaces } from "../../features/operator/system-agent-space";
import { OperatorOnlyDisabled, OperatorOnlyMenuItem } from "../../features/operator-only";
import { useFeature } from "../../features/use-feature";
import { useInboxUnread } from "../../features/work/inbox-list";
import { SharedMarker } from "../../features/work/space-shares";
import { keepFocus } from "../../focus/focus";

type Creating = false | "channel" | "room" | "dm";

export function ChatSidebar({ company, children }: SidebarProps) {
  const feature = useFeature("chat");
  return feature.enabled ? (
    <ChatPanel>{children}</ChatPanel>
  ) : (
    <SidebarPanel title="Chat">
      <p className="px-4 py-1 text-xs text-muted-foreground">
        The Chat add-on is off for {company.name}.
      </p>
      {children}
    </SidebarPanel>
  );
}

function ChatPanel({ children }: { children?: ReactNode }) {
  const chat = useChat();
  const [grouping, setGrouping] = useState<ChannelGrouping>(() => readGrouping(chat.co));
  const lists = useChatLists(chat, grouping);
  const present = usePresence(chat.api, chat.co);
  // CHAT-SWITCH-A: people's active / away / do-not-disturb and status; the viewer's time zone.
  const statuses = useStatuses(chat.api, chat.co, chat.meId !== null);
  useTimeZoneSync(chat);
  // UX-A 0.4: the Activity row is gone, but Chat still keeps the rail's Inbox count fresh (the
  // rail reads the cache only).
  useInboxUnread(chat.api, chat.co, true);
  const current = useParams({ strict: false }) as { channelId?: string; topicId?: string };
  const [creating, setCreating] = useState<Creating>(false);
  const canWrite = chat.meId !== null && !chat.readOnly;
  // OPERATOR-ONLY-UX: the create controls stay visible, unavailable, with the reason.
  const operatorOnly = !chat.readOnly && isOperatorOnly(chat.company);
  const params = { slug: chat.company.slug };
  const { sections } = lists;
  // CHAT-PRESENCE (2026-10-29): Direct messages lists the open conversations only — with people
  // and with DEs, newest activity first, with unread counts. A DM shows once a message is sent or
  // received (the open one shows while it is open); one the viewer closed stays out until someone
  // writes in it or the viewer opens it again (Team, ⌘K, a link). A DE's DM is also its Team row.
  const recentDms = sections.dms.filter(
    (c) => c.last_message_at !== null || c.id === current.channelId,
  );
  const [cleaning, setCleaning] = useState(false);
  // CHAT-PRESENCE: every DE's ring for the viewer (Team, DM rows and their hover cards).
  const presence = useDePresence(chat.api, chat.co, chat.meId !== null || chat.readOnly);
  useReopenOnVisit(chat, lists.list, current.channelId);
  // The open channel's topics are disclosed; a chevron opens another's or closes them. Those
  // choices last until the person moves to another channel.
  const [toggled, setToggled] = useState<ReadonlySet<string>>(() => new Set());
  const channelId = current.channelId;
  useEffect(() => {
    if (channelId !== undefined) setToggled(new Set());
  }, [channelId]);
  const isOpen = (id: string) => (id === channelId) !== toggled.has(id);
  const toggle = (id: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const row = (c: SidebarChannel, lead?: ReactNode, badge?: ReactNode) => (
    <ChannelRow
      key={c.id}
      chat={chat}
      channel={c}
      unread={shownUnread(c, lists.prefs.get(c.id))}
      active={current.channelId === c.id && current.topicId === undefined}
      {...(lead ? { lead } : {})}
      {...(badge ? { badge } : {})}
    />
  );
  const dmOther = (c: SidebarChannel) => dmMemberIds(c).find((id) => id !== chat.meId);
  const channelRow = (c: SidebarChannel, inSpace?: string) => (
    <ChannelWithTopics
      key={c.id}
      chat={chat}
      channel={c}
      unread={shownUnread(c, lists.prefs.get(c.id))}
      muted={lists.prefs.get(c.id)?.muted ?? false}
      current={current}
      open={isOpen(c.id)}
      onToggle={() => toggle(c.id)}
      // MULTI-SPACE-ACCESS: a channel listed under a space it is shared into is marked.
      sharedFrom={
        inSpace && c.space_id !== inSpace
          ? (lists.spaces.data?.data.find((s) => s.id === c.space_id)?.name ?? null)
          : undefined
      }
    />
  );
  const dmLead = (c: SidebarChannel) => {
    // A DM's key is its members' ids, sorted and joined (spec 06 "DMs").
    const other = dmOther(c);
    const kind = other ? chat.members.get(other)?.kind : undefined;
    // CHAT-PRESENCE: a DE's DM shows the DE's ring.
    if (other && kind === "de" && c.kind === "dm")
      return (
        <DeAvatar
          name={channelLabel(c, chat)}
          state={presence.get(other)?.state}
          hint={ringHint(presence.get(other))}
        />
      );
    // CHAT-SWITCH-A: a person's dot is their status (active, away, do not disturb); a DE's, as
    // before, shows while it is connected.
    const status = other && kind !== "de" ? statuses.get(other) : undefined;
    return (
      <span className="relative inline-flex">
        <AvatarTile
          name={channelLabel(c, chat)}
          kind={kind === "de" ? "agent" : "person"}
          size="xs"
          aria-hidden="true"
        />
        {status ? (
          <PresenceDot state={presenceState(status)} className="absolute -right-0.5 -bottom-0.5" />
        ) : other && present.has(other) ? (
          <PresenceDot className="absolute -right-0.5 -bottom-0.5" />
        ) : null}
      </span>
    );
  };
  const browse = (
    <DropdownMenuItem
      render={<Link to="/c/$slug/chat/browse" params={params} search={keepFocus} />}
    >
      <CompassIcon />
      Browse channels
    </DropdownMenuItem>
  );
  // CHAT-SWITCH-A: Slack's search page (words and filters).
  const searchRow = (
    <NavRow
      icon={SearchIcon}
      label="Search"
      data-slot="sidebar-search"
      render={
        <Link
          to="/c/$slug/chat/search"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
  // CHAT-DM: Slack's People — the company directory, with Message on each row.
  const peopleRow = (
    <NavRow
      icon={UsersIcon}
      label="People"
      data-slot="sidebar-people"
      render={
        <Link
          to="/c/$slug/chat/people"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
  return (
    <SidebarPanel
      title="Chat"
      // SIDEBAR-TOGGLE-FIXED: collapsed, Search, Threads and People, then every channel and
      // open DM as an icon (name on hover, a dot when unread).
      strip={
        <>
          <NavList>
            {searchRow}
            {chat.meId !== null ? <ThreadsRow chat={chat} /> : null}
            {peopleRow}
          </NavList>
          {sections.groups.length ? (
            <>
              <SidebarCaption label="Channels" />
              <NavList aria-label="Channels">
                {sections.groups.flatMap((g) => g.channels).map((c) => row(c))}
              </NavList>
            </>
          ) : null}
          {recentDms.length ? (
            <>
              <SidebarCaption label="Direct messages" />
              <NavList aria-label="Direct messages">
                {recentDms.map((c) => row(c, dmLead(c)))}
              </NavList>
            </>
          ) : null}
          {chat.agents && sections.rooms.length ? (
            <>
              <SidebarCaption label="Agent rooms" />
              <NavList aria-label="Agent rooms">{sections.rooms.map((c) => row(c))}</NavList>
            </>
          ) : null}
        </>
      }
      actions={
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Saved messages"
            title="Saved messages"
            nativeButton={false}
            className="text-faint hover:text-foreground aria-[current=page]:bg-primary-soft aria-[current=page]:text-primary-text"
            render={
              <Link
                to="/c/$slug/chat/saved"
                params={params}
                search={keepFocus}
                activeOptions={{ includeSearch: false }}
              />
            }
          >
            <BookmarkIcon />
          </Button>
          {canWrite ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={<Button variant="ghost" size="icon-sm" aria-label="New" />}
              >
                <PlusIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuItem onClick={() => setCreating("channel")}>
                  <PlusIcon />
                  New channel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCreating("dm")}>
                  <MessageSquarePlusIcon />
                  New message
                </DropdownMenuItem>
                {browse}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : operatorOnly ? (
            // OPERATOR-ONLY-UX: channels, messages and rooms are a person member's writes; the
            // control stays visible, disabled, with the reason.
            <OperatorOnlyDisabled>
              <Button variant="ghost" size="icon-sm" aria-label="New">
                <PlusIcon />
              </Button>
            </OperatorOnlyDisabled>
          ) : null}
        </>
      }
    >
      {lists.channels.isError ? (
        <p className="px-4 py-1 text-xs text-muted-foreground">Channels could not be loaded.</p>
      ) : null}
      <NavList className="mt-3" data-slot="sidebar-views">
        <MyStatusRow chat={chat} />
        {searchRow}
        {sections.unreads.length ? (
          <NavRow
            icon={MailOpenIcon}
            label="Unreads"
            count={sections.unreads.reduce((n, c) => n + shownUnread(c, lists.prefs.get(c.id)), 0)}
            data-slot="sidebar-unreads"
            render={
              <Link
                to="/c/$slug/chat/unreads"
                params={params}
                search={keepFocus}
                activeOptions={{ includeSearch: false }}
              />
            }
          />
        ) : null}
        {chat.meId !== null ? <ThreadsRow chat={chat} /> : null}
        {peopleRow}
      </NavList>
      <SidebarCaption
        label="Channels"
        className="mt-2"
        action={
          <SectionMenu label="Channels: add">
            {canWrite ? (
              <DropdownMenuItem onClick={() => setCreating("channel")}>
                <PlusIcon />
                Create channel
              </DropdownMenuItem>
            ) : operatorOnly ? (
              <OperatorOnlyMenuItem icon={<PlusIcon />} label="Create channel" />
            ) : null}
            {browse}
            <DropdownMenuItem
              render={<Link to="/c/$slug/chat/archived" params={params} search={keepFocus} />}
            >
              <ArchiveRestoreIcon />
              Archived channels
            </DropdownMenuItem>
            {sections.groups.length > 1 || sections.grouped ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={sections.grouped}
                  onCheckedChange={(on) => {
                    const next = on ? "space" : "flat";
                    setGrouping(next);
                    writeGrouping(chat.co, next);
                  }}
                >
                  Group by space
                </DropdownMenuCheckboxItem>
              </>
            ) : null}
          </SectionMenu>
        }
      />
      {sections.groups.map((g) => (
        <div key={g.space.id || "other"}>
          {sections.grouped ? (
            <div className="truncate px-4 pt-1.5 pb-0.5 text-xs text-muted-foreground">
              {g.space.name}
            </div>
          ) : null}
          <NavList data-slot="sidebar-channels" data-space={g.space.name}>
            {g.channels.map((c) => channelRow(c, sections.grouped ? g.space.id : undefined))}
          </NavList>
        </div>
      ))}
      {lists.ready && !sections.groups.length ? (
        lists.list.length ? (
          <p className="px-4 py-1 text-xs text-muted-foreground">No channels joined.</p>
        ) : (
          <SidebarEmpty things="channels" {...(canWrite ? { action: "Add one with +" } : {})} />
        )
      ) : null}
      <SidebarCaption
        label="Direct messages"
        action={
          canWrite ? (
            <div className="flex items-center gap-px">
              {/* CHAT-PRESENCE: "Clean up" closes quiet conversations (a modal). */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Direct messages: more"
                      title="More"
                      className="text-faint hover:text-foreground"
                    />
                  }
                >
                  <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-44">
                  <DropdownMenuItem onClick={() => setCleaning(true)}>
                    <BrushCleaningIcon />
                    Clean up…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* CHAT-DM: "+" opens the member picker at once (every member, no space needed). */}
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Direct messages: new"
                title="New message"
                className="text-faint hover:text-foreground"
                onClick={() => setCreating("dm")}
              >
                <PlusIcon />
              </Button>
            </div>
          ) : operatorOnly ? (
            <SectionMenu label="Direct messages: new">
              <OperatorOnlyMenuItem icon={<MessageSquarePlusIcon />} label="New message" />
            </SectionMenu>
          ) : null
        }
      />
      {recentDms.length ? (
        <NavList data-slot="sidebar-dms">
          {recentDms.map((c) => {
            // CHAT-SWITCH-A: the other person's status beside a one-to-one DM.
            const other = dmOther(c);
            const de = other && c.kind === "dm" && chat.members.get(other)?.kind === "de";
            return (
              <DmRow
                key={c.id}
                chat={chat}
                channel={c}
                active={current.channelId === c.id}
                deCard={
                  de && other
                    ? { id: other, name: channelLabel(c, chat), presence: presence.get(other) }
                    : null
                }
              >
                {row(
                  c,
                  dmLead(c),
                  other && !de ? <StatusEmoji status={statuses.get(other)} /> : undefined,
                )}
              </DmRow>
            );
          })}
        </NavList>
      ) : lists.ready ? (
        <SidebarEmpty
          things="direct messages"
          {...(canWrite ? { action: "Start one with +" } : {})}
        />
      ) : null}
      <TeamSection
        chat={chat}
        dms={sections.dms}
        prefs={lists.prefs}
        channels={lists.list}
        current={current}
        present={present}
        statuses={statuses}
        presence={presence}
        ready={lists.ready}
      />
      {chat.agents ? (
        <>
          <SidebarCaption
            label="Agent rooms"
            action={
              canWrite ? (
                <SectionMenu label="Agent rooms: new room">
                  <DropdownMenuItem onClick={() => setCreating("room")}>
                    <BotIcon />
                    New agent room
                  </DropdownMenuItem>
                </SectionMenu>
              ) : operatorOnly ? (
                <SectionMenu label="Agent rooms: new room">
                  <OperatorOnlyMenuItem icon={<BotIcon />} label="New agent room" />
                </SectionMenu>
              ) : null
            }
          />
          {sections.rooms.length ? (
            <NavList data-slot="sidebar-rooms">{sections.rooms.map((c) => channelRow(c))}</NavList>
          ) : lists.ready ? (
            <SidebarEmpty
              things="agent rooms"
              {...(canWrite ? { action: "Add one with +" } : {})}
            />
          ) : null}
        </>
      ) : null}
      {/* SYSTEM-AGENT: a platform super admin acting here sees its private space(s); nobody else. */}
      {operatorOnly ? (
        <SuperAdminSpaces api={chat.api} companyId={chat.co} slug={chat.company.slug} />
      ) : null}
      {children}
      {creating === "channel" || creating === "room" ? (
        <NewChannelDialog
          chat={chat}
          room={creating === "room"}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {creating === "dm" ? (
        <NewMessageDialog chat={chat} onClose={() => setCreating(false)} />
      ) : null}
      {cleaning ? (
        <DmCleanupDialog
          chat={chat}
          // Every DM of the viewer, as the server counts them (empty and archived ones too).
          dms={lists.list.filter((c) => isDm(c) && c.joined && !c.closed)}
          presence={presence}
          onClose={() => setCleaning(false)}
        />
      ) : null}
    </SidebarPanel>
  );
}

/**
 * CHAT-PRESENCE: a DM row with its hover × (close: history kept; it opens again when either side
 * writes, or from Team or ⌘K). A DE's DM opens the DE's hover card.
 */
function DmRow({
  chat,
  channel,
  active,
  deCard,
  children,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  active: boolean;
  deCard: { id: string; name: string; presence: DePresenceRow | undefined } | null;
  children: ReactNode;
}) {
  const close = useCloseDm(chat, channel, active);
  const label = channelLabel(channel, chat);
  const canWrite = chat.meId !== null && !chat.readOnly;
  const list = <ul className="m-0 flex list-none flex-col gap-px p-0">{children}</ul>;
  return (
    <li data-slot="nav-row" data-dm-item={channel.id} className="group/dm relative">
      {deCard ? (
        <DeHoverCard
          api={chat.api}
          co={chat.co}
          slug={chat.company.slug}
          de={{ id: deCard.id, name: deCard.name }}
          presence={deCard.presence}
          canAct={canWrite}
          render={<div />}
        >
          {list}
        </DeHoverCard>
      ) : (
        list
      )}
      {canWrite ? (
        <div
          data-slot="dm-row-actions"
          className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-px rounded-md bg-sidebar/95 opacity-0 group-hover/dm:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100"
        >
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Close conversation with ${label}`}
            title="Close (history is kept)"
            className="text-faint hover:text-foreground"
            disabled={close.isPending}
            onClick={() => close.mutate()}
          >
            <XIcon />
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Threads (CHAT-CONTROLS; Slack's): the person's threads. THREAD-READS: with new replies the
 * label is bold and an accent pill counts the threads that have them (as an unread channel).
 */
function ThreadsRow({ chat }: { chat: ChatContext }) {
  const threads = useQuery({ ...myThreadsQuery(chat.api, chat.co), retry: false });
  const unread = threads.data?.data.filter((t) => t.unread).length ?? 0;
  return (
    <NavRow
      icon={MessagesSquareIcon}
      label="Threads"
      dot={unread > 0}
      data-unread={unread > 0 ? "true" : undefined}
      className={unread > 0 ? "font-semibold text-foreground" : undefined}
      trailing={
        unread > 0 ? (
          <CountPill
            count={unread}
            tone="accent"
            aria-label={`${unread} ${unread === 1 ? "thread" : "threads"} with new replies`}
          />
        ) : null
      }
      data-slot="sidebar-threads"
      render={
        <Link
          to="/c/$slug/chat/threads"
          params={{ slug: chat.company.slug }}
          search={(prev: Record<string, unknown>) => {
            const { thread: _thread, ...rest } = keepFocus(prev);
            return rest;
          }}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
}

/** A caption's small "+" menu. */
function SectionMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            className="text-faint hover:text-foreground"
          />
        }
      >
        <PlusIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ChannelRow({
  chat,
  channel,
  unread,
  active,
  lead,
  badge,
  className,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  unread: number;
  active: boolean;
  lead?: ReactNode;
  /** After the name (CHAT-SWITCH-A: a DM person's status emoji). */
  badge?: ReactNode;
  className?: string | undefined;
}) {
  const queryClient = useQueryClient();
  const warm = () => prefetchChannel(queryClient, chat.api, chat.co, channel.id);
  // The # icon already says "channel": the row reads "general", not "# #general". Its
  // accessible name keeps "#general" (and the unread count). CHAT-PRESENCE: a DM's names drop
  // the "DE ·" prefix (the ring says what it is).
  const label = isDm(channel)
    ? channelLabel(channel, chat).split(", ").map(workerName).join(", ")
    : channelLabel(channel, chat);
  return (
    <NavRow
      {...(lead ? { lead } : { icon: channelIcon(channel) })}
      {...(badge ? { badge } : {})}
      label={isDm(channel) ? label : channel.name}
      aria-label={unread > 0 ? `${label}, ${unread} unread` : label}
      active={active}
      dot={unread > 0}
      data-unread={unread > 0 ? "true" : undefined}
      className={["text-base", unread > 0 ? "font-semibold text-foreground" : "", className ?? ""]
        .filter(Boolean)
        .join(" ")}
      trailing={
        unread > 0 ? (
          <CountPill count={unread} tone="accent" aria-label={`${unread} unread`} />
        ) : null
      }
      render={
        <Link
          to="/c/$slug/chat/$channelId"
          params={{ slug: chat.company.slug, channelId: channel.id }}
          search={keepFocus}
          activeOptions={{ includeSearch: false, exact: true }}
          // CHAT-PERF: load what the channel shows on intent; the click renders from cache.
          onMouseEnter={warm}
          onFocus={warm}
        />
      }
    />
  );
}

/**
 * A channel row with its disclosure: when the channel has topics a small chevron sits over the
 * row's left edge (a sibling of the link, never inside it); open, the topics list beneath.
 */
function ChannelWithTopics({
  chat,
  channel,
  unread,
  muted,
  current,
  open,
  onToggle,
  sharedFrom,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  unread: number;
  muted: boolean;
  current: { channelId?: string; topicId?: string };
  open: boolean;
  onToggle: () => void;
  /** MULTI-SPACE-ACCESS: listed in a space it is shared into: its home space's name. */
  sharedFrom?: string | null | undefined;
}) {
  // One small cached call per listed channel (the channel screen reads the same one); the
  // server adds the archived topics for the channel's managers only.
  const topics = useQuery({
    ...topicsQuery(chat.api, chat.co, channel.id),
    enabled: open || channel.last_message_at !== null,
  });
  // CHAT-TOPICS-SEPARATE: the channel's own conversation is the channel row, never a topic (a
  // private topic the caller is not in never comes from the API).
  const all = realTopics(topics.data?.data ?? []);
  const list = orderedTopics(all);
  const archived = archivedTopics(all);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const has = list.length > 0 || archived.length > 0;
  const expanded = open && has;
  const label = channelLabel(channel, chat);
  const canWrite = chat.meId !== null && !chat.readOnly;
  return (
    <li data-slot="nav-row" className="relative">
      <div className="group/channel relative">
        <ul className="m-0 flex list-none flex-col gap-px p-0">
          <ChannelRow
            chat={chat}
            channel={channel}
            unread={unread}
            active={current.channelId === channel.id && current.topicId === undefined}
            {...(sharedFrom !== undefined ? { badge: <SharedMarker home={sharedFrom} /> } : {})}
          />
        </ul>
        {canWrite ? (
          <div
            data-slot="channel-row-actions"
            className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-px rounded-md bg-sidebar/95 opacity-0 group-hover/channel:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:opacity-100"
          >
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`New topic in ${label}`}
              title="New topic"
              onClick={() => setCreating(true)}
              className="text-faint hover:text-foreground"
            >
              <PlusIcon />
            </Button>
            <ChannelRowMenu
              chat={chat}
              channel={channel}
              muted={muted}
              active={current.channelId === channel.id}
            />
          </div>
        ) : null}
        {has ? (
          <button
            type="button"
            aria-label={`${expanded ? "Collapse" : "Expand"} topics of ${label}`}
            aria-expanded={expanded}
            onClick={onToggle}
            className="absolute top-1/2 left-0 flex size-4 -translate-y-1/2 items-center justify-center rounded text-faint opacity-60 hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <ChevronRightIcon
              aria-hidden="true"
              className={`size-3.5 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </button>
        ) : null}
      </div>
      {creating ? (
        <NewTopicDialog chat={chat} channel={channel} onClose={() => setCreating(false)} />
      ) : null}
      {expanded ? (
        <ul
          data-slot="sidebar-topics"
          aria-label={`Topics of ${label}`}
          className="m-0 flex list-none flex-col gap-px p-0 pb-1"
        >
          {list.map((t) => (
            <TopicRow
              key={t.id}
              chat={chat}
              channel={channel}
              topic={t}
              active={current.topicId === t.id}
              muted={muted}
            />
          ))}
          {archived.length ? (
            <li data-slot="nav-row" className="relative">
              <button
                type="button"
                aria-expanded={archivedOpen}
                onClick={() => setArchivedOpen((o) => !o)}
                className="flex min-h-7 w-full items-center gap-1.5 rounded-md pr-2 pl-9 text-left text-xs text-faint hover:bg-muted hover:text-foreground pointer-coarse:min-h-9"
              >
                <ChevronRightIcon
                  aria-hidden="true"
                  className={`size-3 shrink-0 transition-transform ${archivedOpen ? "rotate-90" : ""}`}
                />
                Archived ({archived.length})
              </button>
              {archivedOpen ? (
                <ul
                  data-slot="sidebar-archived-topics"
                  aria-label={`Archived topics of ${label}`}
                  className="m-0 flex list-none flex-col gap-px p-0"
                >
                  {archived.map((t) => (
                    <TopicRow
                      key={t.id}
                      chat={chat}
                      channel={channel}
                      topic={t}
                      active={current.topicId === t.id}
                      muted
                    />
                  ))}
                </ul>
              ) : null}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * A channel row's "…" (CHAT-CONTROLS; Slack's): Channel details, Rename (managers; the details
 * on About), Mute / Unmute, Leave (not a default channel) and Archive (managers). The managers'
 * items are decided when the menu opens (one small call for non-admins), never per row.
 */
function ChannelRowMenu({
  chat,
  channel,
  muted,
  active,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  muted: boolean;
  active: boolean;
}) {
  const [details, setDetails] = useState<DetailsTab | null>(null);
  const label = channelLabel(channel);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Channel menu: ${label}`}
              title="More"
              className="text-faint hover:text-foreground"
            />
          }
        >
          <EllipsisIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <ChannelRowMenuItems
            chat={chat}
            channel={channel}
            muted={muted}
            active={active}
            onDetails={setDetails}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {details ? (
        <ChannelDetailsDialog
          chat={chat}
          channel={channel}
          tab={details}
          onClose={() => setDetails(null)}
        />
      ) : null}
    </>
  );
}

function ChannelRowMenuItems({
  chat,
  channel,
  muted,
  active,
  onDetails,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  muted: boolean;
  active: boolean;
  onDetails: (tab: DetailsTab) => void;
}) {
  // MULTI-SPACE-ACCESS: the row's `can_manage` also counts (a Full access share manages).
  const manage = useCanManage(chat, channel) || channel.can_manage;
  const prefs = usePrefs(chat, channel);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const label = channelLabel(channel);
  const dm = isDm(channel);
  const archive = useMutation({
    mutationFn: () => chat.api.client.channels.archive({ params: { co: chat.co, id: channel.id } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      toast.add({ title: `Archived ${label}`, type: "success" });
      if (active)
        void navigate({
          to: "/c/$slug/chat",
          params: { slug: chat.company.slug },
          search: keepFocus as never,
        });
    },
    onError: (e) =>
      toast.add({ title: "Could not archive the channel", description: e.message, type: "error" }),
  });
  return (
    <>
      <DropdownMenuItem onClick={() => onDetails("about")}>
        <InfoIcon />
        Channel details
      </DropdownMenuItem>
      {manage && !dm && !channel.archived_at ? (
        <DropdownMenuItem onClick={() => onDetails("about")}>
          <PencilIcon />
          Rename
        </DropdownMenuItem>
      ) : null}
      {manage && !dm && !channel.is_private && !channel.archived_at ? (
        <DropdownMenuItem onClick={() => onDetails("spaces")}>
          <Share2Icon />
          Share with spaces
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem
        onClick={() =>
          prefs.save.mutate(
            { muted: !muted },
            {
              onSuccess: () =>
                toast.add({ title: `${muted ? "Unmuted" : "Muted"} ${label}`, type: "success" }),
            },
          )
        }
      >
        {muted ? <BellIcon /> : <BellOffIcon />}
        {muted ? "Unmute" : "Mute"}
      </DropdownMenuItem>
      {!dm && !channel.is_default ? (
        <DropdownMenuItem
          onClick={() =>
            prefs.save.mutate(
              { joined: false },
              { onSuccess: () => toast.add({ title: `Left ${label}`, type: "success" }) },
            )
          }
        >
          <LogOutIcon />
          Leave
        </DropdownMenuItem>
      ) : null}
      {manage && !dm && !channel.is_default && !channel.archived_at ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => archive.mutate()}>
            <ArchiveIcon />
            Archive
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );
}

/**
 * A topic beneath its channel: indented and light; pinned first with a pin, closed greyed,
 * archived struck. Hovered (or focused) it shows its actions: pin, close and "…" — Restore for
 * an archived one.
 */
function TopicRow({
  chat,
  channel,
  topic,
  active,
  muted,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  topic: TopicSummary;
  active: boolean;
  /** The channel is muted: no pills (06: "nothing else blinks"). */
  muted: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const warm = () => prefetchTopic(queryClient, chat.api, chat.co, topic.thread_id);
  const closed = topic.status === "resolved";
  const archived = topic.archived_at !== null;
  // CHAT-TOPICS-SEPARATE: only a member's topics count unread.
  const unread = muted || !topic.is_member ? 0 : topic.unread_count;
  const canWrite = chat.meId !== null && !chat.readOnly;
  return (
    <li data-slot="nav-row" data-topic-row={topic.id} className="group/topic relative">
      <ul className="m-0 flex list-none flex-col gap-px p-0">
        <NavRow
          label={topic.name}
          active={active}
          data-topic-status={topic.status}
          data-topic-archived={archived ? "true" : undefined}
          data-unread={unread > 0 ? "true" : undefined}
          className={`min-h-7 pl-9 text-base pointer-coarse:min-h-9 ${
            archived
              ? "text-faint line-through"
              : closed
                ? "text-muted-foreground"
                : unread > 0
                  ? "font-semibold text-foreground"
                  : ""
          }`}
          lead={
            topic.is_private ? (
              <LockIcon aria-label="Private topic" className="size-3 shrink-0 text-faint" />
            ) : topic.pinned_at ? (
              <PinIcon aria-label="Pinned" className="size-3 shrink-0 text-faint" />
            ) : closed ? (
              <CircleCheckIcon aria-label="Closed" className="size-3 shrink-0 text-faint" />
            ) : (
              <span aria-hidden="true" className="size-3 shrink-0" />
            )
          }
          trailing={
            unread > 0 ? (
              <CountPill count={unread} tone="accent" aria-label={`${unread} unread`} />
            ) : null
          }
          render={
            <Link
              to="/c/$slug/chat/$channelId/$topicId"
              params={{ slug: chat.company.slug, channelId: channel.id, topicId: topic.id }}
              search={keepFocus}
              activeOptions={{ includeSearch: false }}
              // CHAT-PERF: page 1 of the topic's messages on intent.
              onMouseEnter={warm}
              onFocus={warm}
            />
          }
        />
      </ul>
      {canWrite ? (
        <div
          data-slot="topic-row-actions"
          className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-px rounded-md bg-sidebar/95 opacity-0 group-hover/topic:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:opacity-100"
        >
          <TopicQuickActions chat={chat} channel={channel} topic={topic} compact />
          <TopicMenu
            chat={chat}
            channel={channel}
            topic={topic}
            className="text-faint hover:text-foreground"
            onDeleted={
              active
                ? () =>
                    void navigate({
                      to: "/c/$slug/chat/$channelId",
                      params: { slug: chat.company.slug, channelId: channel.id },
                      search: keepFocus as never,
                    })
                : undefined
            }
          />
        </div>
      ) : null}
    </li>
  );
}
